import {
  TrendingUp, MessageSquare, Bookmark, ArrowLeftRight, PieChart,
  Bell, MoreHorizontal, LayoutDashboard, Scale,
} from "lucide-react";

export const API_URL =
  process.env.REACT_APP_API_URL ||
  ((window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://localhost:8001"
    : "https://finance-ai-bt9x.onrender.com");

// ── Color tokens ────────────────────────────────────────
export const C = {
  bg:       "#111113",
  surface:  "#18181B",
  surface2: "#222226",
  border:   "#2E2E32",
  borderA:  "#3F3F44",
  text:     "#C9C9D0",
  textSec:  "#7A7A84",
  textTer:  "#46464E",
  accent:   "#3EB489",
  accentDim:"rgba(62,180,137,0.09)",
  gold:     "#C9A227",
  pos:      "#3DBB6E",
  neg:      "#D95F5A",
  neutral:  "#C9A227",
};

export const WATCHLIST_DEFAULT = [
  { ticker: "RELIANCE.NS",   name: "Reliance Ind.",  price: null, change: null, base: 1430, type: "India" },
  { ticker: "TCS.NS",        name: "TCS",            price: null, change: null, base: 2465, type: "India" },
  { ticker: "HDFCBANK.NS",   name: "HDFC Bank",      price: null, change: null, base: 778, type: "India" },
  { ticker: "INFY.NS",       name: "Infosys",        price: null, change: null, base: 1167, type: "India" },
  { ticker: "ICICIBANK.NS",  name: "ICICI Bank",     price: null, change: null, base: 1279, type: "India" },
  { ticker: "SBIN.NS",       name: "SBI",            price: null, change: null, base: 788,  type: "India" },
  { ticker: "BHARTIARTL.NS", name: "Bharti Airtel",  price: null, change: null, base: 1800, type: "India" },
  { ticker: "WIPRO.NS",      name: "Wipro",          price: null, change: null, base: 450,  type: "India" },
  { ticker: "AXISBANK.NS",   name: "Axis Bank",      price: null, change: null, base: 1100, type: "India" },
  { ticker: "TATAMOTORS.NS", name: "Tata Motors",    price: null, change: null, base: 650,  type: "India" },
  { ticker: "MARUTI.NS",     name: "Maruti Suzuki",  price: null, change: null, base: 12400, type: "India" },
  { ticker: "SUNPHARMA.NS",  name: "Sun Pharma",     price: null, change: null, base: 1650, type: "India" },
];

export const SUGGESTIONS = [
  "Should I buy Reliance now?",
  "Compare HDFCBANK.NS vs ICICIBANK.NS",
  "Analyze portfolio RELIANCE.NS TCS.NS INFY.NS",
  "Is Nifty 50 overbought right now?",
  "What are the best large cap stocks on NSE?",
  "Explain P/E ratio for TCS",
];

export const METRIC_EXPLANATIONS = {
  "Price":     { short: "What one share costs right now.", detail: "The last traded price. Goes up when more people want to buy; goes down when more want to sell. Price alone says nothing about value — compare with P/E and Market Cap." },
  "5D Change": { short: "Price movement over the last 5 trading days.", detail: "Short-term momentum indicator. Green = rising, red = falling. Useful for catching trends, but too short a window for long-term investors." },
  "Mkt Cap":   { short: "Total price tag of the entire company.", detail: "Market Cap = Share Price × Total Shares. Large cap = huge and stable. Mid cap = growing. Small cap = risky but high potential." },
  "P/E":       { short: "Is the stock expensive relative to its earnings?", detail: "P/E of 20 means you pay ₹20 for every ₹1 the company earns per year. Lower can mean cheaper — but very low P/E can signal trouble ahead." },
  "EPS":       { short: "Profit the company makes per share.", detail: "Earnings Per Share = Net Profit ÷ Shares Outstanding. Rising EPS over time is a positive signal." },
  "52W High":  { short: "Highest price this stock hit in the past year.", detail: "If current price is near the 52W high, momentum is strong but the stock may be fully priced." },
  "52W Low":   { short: "Lowest price this stock hit in the past year.", detail: "Shows how far the stock has fallen. Current price near this level warrants research into the cause." },
  "Rel Vol":   { short: "Is trading unusually busy today?", detail: "Relative Volume compares today's volume to the average. >1.5 means unusual activity (news, earnings). High Rel Vol often precedes big moves." },
};

export const TIME_RANGES = [
  { label: "1W", days: 7   },
  { label: "1M", days: 30  },
  { label: "3M", days: 90  },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
];

// ── Nav config ───────────────────────────────────────────
export const NAV_ITEMS = [
  { id: "overview",  label: "Overview",  Icon: LayoutDashboard },
  { id: "market",    label: "Market",    Icon: TrendingUp      },
  { id: "chat",      label: "Chat",      Icon: MessageSquare   },
  { id: "watchlist", label: "Watchlist", Icon: Bookmark        },
  { id: "compare",   label: "Compare",   Icon: ArrowLeftRight  },
  { id: "debate",    label: "Debate",    Icon: Scale           },
  { id: "portfolio", label: "Portfolio", Icon: PieChart        },
  { id: "alerts",    label: "Alerts",    Icon: Bell            },
];

export const MOBILE_TABS = [
  { id: "overview",  label: "Home",      Icon: LayoutDashboard },
  { id: "market",    label: "Market",    Icon: TrendingUp      },
  { id: "chat",      label: "Chat",      Icon: MessageSquare   },
  { id: "more",      label: "More",      Icon: MoreHorizontal  },
];

export const MORE_SECTIONS = ["watchlist", "compare", "debate", "portfolio", "alerts"];

// ── Chart helpers ─────────────────────────────────────────
export const CHART_VALID_TICKER = /^[A-Za-z0-9._:&\-]{1,20}$/;

// ── Ticker autocomplete data ──────────────────────────────
export const TICKER_LIST = [
  // ── Nifty 50 ──────────────────────────────────────────
  { ticker: "RELIANCE.NS",    name: "Reliance Industries" },
  { ticker: "TCS.NS",         name: "Tata Consultancy Services" },
  { ticker: "HDFCBANK.NS",    name: "HDFC Bank" },
  { ticker: "INFY.NS",        name: "Infosys" },
  { ticker: "ICICIBANK.NS",   name: "ICICI Bank" },
  { ticker: "HINDUNILVR.NS",  name: "Hindustan Unilever" },
  { ticker: "SBIN.NS",        name: "State Bank of India" },
  { ticker: "BHARTIARTL.NS",  name: "Bharti Airtel" },
  { ticker: "ITC.NS",         name: "ITC Limited" },
  { ticker: "KOTAKBANK.NS",   name: "Kotak Mahindra Bank" },
  { ticker: "LT.NS",          name: "Larsen & Toubro" },
  { ticker: "AXISBANK.NS",    name: "Axis Bank" },
  { ticker: "ASIANPAINT.NS",  name: "Asian Paints" },
  { ticker: "MARUTI.NS",      name: "Maruti Suzuki" },
  { ticker: "TITAN.NS",       name: "Titan Company" },
  { ticker: "SUNPHARMA.NS",   name: "Sun Pharmaceutical" },
  { ticker: "BAJFINANCE.NS",  name: "Bajaj Finance" },
  { ticker: "BAJAJFINSV.NS",  name: "Bajaj Finserv" },
  { ticker: "NTPC.NS",        name: "NTPC Limited" },
  { ticker: "POWERGRID.NS",   name: "Power Grid Corporation" },
  { ticker: "ONGC.NS",        name: "Oil & Natural Gas Corp" },
  { ticker: "TATAMOTORS.NS",  name: "Tata Motors" },
  { ticker: "TATASTEEL.NS",   name: "Tata Steel" },
  { ticker: "WIPRO.NS",       name: "Wipro" },
  { ticker: "HCLTECH.NS",     name: "HCL Technologies" },
  { ticker: "TECHM.NS",       name: "Tech Mahindra" },
  { ticker: "ULTRACEMCO.NS",  name: "UltraTech Cement" },
  { ticker: "GRASIM.NS",      name: "Grasim Industries" },
  { ticker: "NESTLEIND.NS",   name: "Nestle India" },
  { ticker: "DIVISLAB.NS",    name: "Divi's Laboratories" },
  { ticker: "DRREDDY.NS",     name: "Dr Reddy's Laboratories" },
  { ticker: "CIPLA.NS",       name: "Cipla" },
  { ticker: "EICHERMOT.NS",   name: "Eicher Motors" },
  { ticker: "HEROMOTOCO.NS",  name: "Hero MotoCorp" },
  { ticker: "JSWSTEEL.NS",    name: "JSW Steel" },
  { ticker: "HINDALCO.NS",    name: "Hindalco Industries" },
  { ticker: "BPCL.NS",        name: "Bharat Petroleum" },
  { ticker: "COALINDIA.NS",   name: "Coal India" },
  { ticker: "M&M.NS",         name: "Mahindra & Mahindra" },
  { ticker: "ADANIENT.NS",    name: "Adani Enterprises" },
  { ticker: "ADANIPORTS.NS",  name: "Adani Ports" },
  { ticker: "ADANIGREEN.NS",  name: "Adani Green Energy" },
  { ticker: "INDUSINDBK.NS",  name: "IndusInd Bank" },
  { ticker: "APOLLOHOSP.NS",  name: "Apollo Hospitals" },
  { ticker: "ZOMATO.NS",      name: "Zomato" },
  { ticker: "TATACONSUM.NS",  name: "Tata Consumer Products" },
  { ticker: "PIDILITIND.NS",  name: "Pidilite Industries" },
  { ticker: "SBILIFE.NS",     name: "SBI Life Insurance" },
  { ticker: "HDFCLIFE.NS",    name: "HDFC Life Insurance" },
  { ticker: "ICICIPRULI.NS",  name: "ICICI Prudential Life" },
  { ticker: "SHREECEM.NS",    name: "Shree Cement" },
  { ticker: "LTIM.NS",        name: "LTIMindtree" },
  { ticker: "BAJAJ-AUTO.NS",  name: "Bajaj Auto" },
  // ── Banking & Finance ─────────────────────────────────
  { ticker: "DABUR.NS",       name: "Dabur India" },
  { ticker: "MARICO.NS",      name: "Marico" },
  { ticker: "GODREJCP.NS",    name: "Godrej Consumer Products" },
  { ticker: "VEDL.NS",        name: "Vedanta" },
  { ticker: "BANKBARODA.NS",  name: "Bank of Baroda" },
  { ticker: "PNB.NS",         name: "Punjab National Bank" },
  { ticker: "CANBK.NS",       name: "Canara Bank" },
  { ticker: "UNIONBANK.NS",   name: "Union Bank of India" },
  { ticker: "IDFCFIRSTB.NS",  name: "IDFC First Bank" },
  { ticker: "FEDERALBNK.NS",  name: "Federal Bank" },
  { ticker: "BANDHANBNK.NS",  name: "Bandhan Bank" },
  { ticker: "RBLBANK.NS",     name: "RBL Bank" },
  { ticker: "YESBANK.NS",     name: "Yes Bank" },
  { ticker: "AUBANK.NS",      name: "AU Small Finance Bank" },
  { ticker: "CHOLAFIN.NS",    name: "Cholamandalam Finance" },
  { ticker: "BAJAJHLDNG.NS",  name: "Bajaj Holdings" },
  { ticker: "MUTHOOTFIN.NS",  name: "Muthoot Finance" },
  { ticker: "MANAPPURAM.NS",  name: "Manappuram Finance" },
  { ticker: "SHRIRAMFIN.NS",  name: "Shriram Finance" },
  { ticker: "LICIHSGFIN.NS",  name: "LIC Housing Finance" },
  { ticker: "PNBHOUSING.NS",  name: "PNB Housing Finance" },
  { ticker: "ICICIGI.NS",     name: "ICICI Lombard Insurance" },
  { ticker: "NIACL.NS",       name: "New India Assurance" },
  { ticker: "HDFCAMC.NS",     name: "HDFC AMC" },
  { ticker: "NAM-INDIA.NS",   name: "Nippon India Mutual Fund" },
  // ── IT & Tech ─────────────────────────────────────────
  { ticker: "MPHASIS.NS",     name: "Mphasis" },
  { ticker: "COFORGE.NS",     name: "Coforge" },
  { ticker: "PERSISTENT.NS",  name: "Persistent Systems" },
  { ticker: "KPITTECH.NS",    name: "KPIT Technologies" },
  { ticker: "TANLA.NS",       name: "Tanla Platforms" },
  { ticker: "INTELLECT.NS",   name: "Intellect Design Arena" },
  { ticker: "ZENSARTECH.NS",  name: "Zensar Technologies" },
  { ticker: "MASTEK.NS",      name: "Mastek" },
  { ticker: "NIITLTD.NS",     name: "NIIT" },
  { ticker: "RATEGAIN.NS",    name: "RateGain Travel Tech" },
  // ── Auto & EV ─────────────────────────────────────────
  { ticker: "TVSMOTOR.NS",    name: "TVS Motor Company" },
  { ticker: "ASHOKLEY.NS",    name: "Ashok Leyland" },
  { ticker: "BHARATFORG.NS",  name: "Bharat Forge" },
  { ticker: "MOTHERSON.NS",   name: "Samvardhana Motherson" },
  { ticker: "BOSCHLTD.NS",    name: "Bosch India" },
  { ticker: "EXIDEIND.NS",    name: "Exide Industries" },
  { ticker: "AMARAJABAT.NS",  name: "Amara Raja Energy" },
  { ticker: "MRF.NS",         name: "MRF" },
  { ticker: "CEATLTD.NS",     name: "CEAT" },
  { ticker: "OLECTRA.NS",     name: "Olectra Greentech" },
  { ticker: "OLAMOBILITY.NS", name: "Ola Electric" },
  // ── Healthcare & Pharma ───────────────────────────────
  { ticker: "AUROPHARMA.NS",  name: "Aurobindo Pharma" },
  { ticker: "LUPIN.NS",       name: "Lupin" },
  { ticker: "TORNTPHARM.NS",  name: "Torrent Pharmaceuticals" },
  { ticker: "ALKEM.NS",       name: "Alkem Laboratories" },
  { ticker: "IPCALAB.NS",     name: "IPCA Laboratories" },
  { ticker: "GRANULES.NS",    name: "Granules India" },
  { ticker: "LAURUSLABS.NS",  name: "Laurus Labs" },
  { ticker: "PFIZER.NS",      name: "Pfizer India" },
  { ticker: "ABBOTINDIA.NS",  name: "Abbott India" },
  { ticker: "METROPOLIS.NS",  name: "Metropolis Healthcare" },
  { ticker: "DRLABEQ.NS",     name: "Dr Lal PathLabs" },
  { ticker: "MAXHEALTH.NS",   name: "Max Healthcare" },
  { ticker: "FORTIS.NS",      name: "Fortis Healthcare" },
  // ── Infra, Defence & PSU ──────────────────────────────
  { ticker: "IRCTC.NS",       name: "IRCTC" },
  { ticker: "DMART.NS",       name: "Avenue Supermarts (DMart)" },
  { ticker: "HAL.NS",         name: "Hindustan Aeronautics" },
  { ticker: "BEL.NS",         name: "Bharat Electronics" },
  { ticker: "BHEL.NS",        name: "BHEL" },
  { ticker: "RVNL.NS",        name: "Rail Vikas Nigam" },
  { ticker: "IRFC.NS",        name: "Indian Railway Finance Corp" },
  { ticker: "RAILTEL.NS",     name: "RailTel Corporation" },
  { ticker: "CONCOR.NS",      name: "Container Corporation" },
  { ticker: "SAIL.NS",        name: "Steel Authority of India" },
  { ticker: "NMDC.NS",        name: "NMDC Steel" },
  { ticker: "SJVN.NS",        name: "SJVN Limited" },
  { ticker: "NHPC.NS",        name: "NHPC Limited" },
  { ticker: "RITES.NS",       name: "RITES Limited" },
  { ticker: "IREDA.NS",       name: "IREDA" },
  { ticker: "RECLTD.NS",      name: "REC Limited" },
  { ticker: "PFC.NS",         name: "Power Finance Corporation" },
  { ticker: "TATAPOWER.NS",   name: "Tata Power" },
  { ticker: "ADANITRANS.NS",  name: "Adani Transmission" },
  { ticker: "CESC.NS",        name: "CESC Limited" },
  { ticker: "JSL.NS",         name: "Jindal Stainless" },
  // ── Consumer & FMCG ──────────────────────────────────
  { ticker: "EMAMILTD.NS",    name: "Emami" },
  { ticker: "TATACOMM.NS",    name: "Tata Communications" },
  { ticker: "COLPAL.NS",      name: "Colgate-Palmolive India" },
  { ticker: "PGHH.NS",        name: "Procter & Gamble India" },
  { ticker: "GILLETTE.NS",    name: "Gillette India" },
  { ticker: "BRITANNIA.NS",   name: "Britannia Industries" },
  { ticker: "VBL.NS",         name: "Varun Beverages" },
  { ticker: "UBL.NS",         name: "United Breweries" },
  { ticker: "RADICO.NS",      name: "Radico Khaitan" },
  { ticker: "UNITDSPR.NS",    name: "United Spirits" },
  // ── New-age / Startups ────────────────────────────────
  { ticker: "NYKAA.NS",       name: "Nykaa (FSN E-Commerce)" },
  { ticker: "PAYTM.NS",       name: "Paytm (One97 Communications)" },
  { ticker: "DELHIVERY.NS",   name: "Delhivery" },
  { ticker: "POLICYBZR.NS",   name: "PolicyBazaar (PB Fintech)" },
  { ticker: "CARTRADE.NS",    name: "CarTrade Tech" },
  { ticker: "EASEMYTRIP.NS",  name: "EaseMyTrip" },
  { ticker: "INDIGO.NS",      name: "IndiGo (InterGlobe Aviation)" },
  { ticker: "SPICEJET.NS",    name: "SpiceJet" },
  // ── Realty ────────────────────────────────────────────
  { ticker: "DLF.NS",         name: "DLF" },
  { ticker: "GODREJPROP.NS",  name: "Godrej Properties" },
  { ticker: "OBEROIRLTY.NS",  name: "Oberoi Realty" },
  { ticker: "PRESTIGE.NS",    name: "Prestige Estates" },
  { ticker: "PHOENIXLTD.NS",  name: "Phoenix Mills" },
  { ticker: "BRIGADE.NS",     name: "Brigade Enterprises" },
  // ── Chemicals & Materials ─────────────────────────────
  { ticker: "ATUL.NS",        name: "Atul Ltd" },
  { ticker: "SRF.NS",         name: "SRF Limited" },
  { ticker: "NAVINFLUOR.NS",  name: "Navin Fluorine" },
  { ticker: "DEEPAKNTR.NS",   name: "Deepak Nitrite" },
  { ticker: "TATACHEM.NS",    name: "Tata Chemicals" },
  { ticker: "GNFC.NS",        name: "GNFC" },
  { ticker: "BASF.NS",        name: "BASF India" },
  // ── US Mega-cap ───────────────────────────────────────
  { ticker: "AAPL",           name: "Apple" },
  { ticker: "MSFT",           name: "Microsoft" },
  { ticker: "GOOGL",          name: "Alphabet (Google)" },
  { ticker: "AMZN",           name: "Amazon" },
  { ticker: "NVDA",           name: "NVIDIA" },
  { ticker: "META",           name: "Meta Platforms" },
  { ticker: "TSLA",           name: "Tesla" },
  { ticker: "NFLX",           name: "Netflix" },
  { ticker: "AMD",            name: "Advanced Micro Devices" },
  { ticker: "INTC",           name: "Intel" },
  { ticker: "ORCL",          name: "Oracle" },
  { ticker: "CRM",            name: "Salesforce" },
  { ticker: "ADBE",           name: "Adobe" },
  { ticker: "QCOM",           name: "Qualcomm" },
  { ticker: "AVGO",           name: "Broadcom" },
  { ticker: "TXN",            name: "Texas Instruments" },
  { ticker: "MU",             name: "Micron Technology" },
  { ticker: "AMAT",           name: "Applied Materials" },
  { ticker: "LRCX",           name: "Lam Research" },
  { ticker: "KLAC",           name: "KLA Corporation" },
  // ── US Finance ────────────────────────────────────────
  { ticker: "JPM",            name: "JPMorgan Chase" },
  { ticker: "BAC",            name: "Bank of America" },
  { ticker: "GS",             name: "Goldman Sachs" },
  { ticker: "MS",             name: "Morgan Stanley" },
  { ticker: "V",              name: "Visa" },
  { ticker: "MA",             name: "Mastercard" },
  { ticker: "AXP",            name: "American Express" },
  { ticker: "WFC",            name: "Wells Fargo" },
  { ticker: "C",              name: "Citigroup" },
  { ticker: "BRK-B",          name: "Berkshire Hathaway B" },
  { ticker: "BLK",            name: "BlackRock" },
  { ticker: "SCHW",           name: "Charles Schwab" },
  { ticker: "PYPL",           name: "PayPal" },
  { ticker: "SQ",             name: "Block (Square)" },
  { ticker: "COIN",           name: "Coinbase" },
  // ── US Healthcare ─────────────────────────────────────
  { ticker: "JNJ",            name: "Johnson & Johnson" },
  { ticker: "LLY",            name: "Eli Lilly" },
  { ticker: "PFE",            name: "Pfizer" },
  { ticker: "MRK",            name: "Merck" },
  { ticker: "ABBV",           name: "AbbVie" },
  { ticker: "BMY",            name: "Bristol-Myers Squibb" },
  { ticker: "UNH",            name: "UnitedHealth Group" },
  { ticker: "CVS",            name: "CVS Health" },
  { ticker: "AMGN",           name: "Amgen" },
  { ticker: "GILD",           name: "Gilead Sciences" },
  // ── US Consumer & Retail ─────────────────────────────
  { ticker: "WMT",            name: "Walmart" },
  { ticker: "COST",           name: "Costco" },
  { ticker: "TGT",            name: "Target" },
  { ticker: "HD",             name: "Home Depot" },
  { ticker: "MCD",            name: "McDonald's" },
  { ticker: "SBUX",           name: "Starbucks" },
  { ticker: "NKE",            name: "Nike" },
  { ticker: "KO",             name: "Coca-Cola" },
  { ticker: "PEP",            name: "PepsiCo" },
  { ticker: "DIS",            name: "Walt Disney" },
  { ticker: "UBER",           name: "Uber" },
  { ticker: "LYFT",           name: "Lyft" },
  { ticker: "ABNB",           name: "Airbnb" },
  { ticker: "BKNG",           name: "Booking Holdings" },
  // ── US Energy ─────────────────────────────────────────
  { ticker: "XOM",            name: "ExxonMobil" },
  { ticker: "CVX",            name: "Chevron" },
  { ticker: "COP",            name: "ConocoPhillips" },
  { ticker: "SLB",            name: "SLB (Schlumberger)" },
  // ── ETFs ──────────────────────────────────────────────
  { ticker: "SPY",            name: "S&P 500 ETF (SPDR)" },
  { ticker: "QQQ",            name: "Nasdaq 100 ETF (Invesco)" },
  { ticker: "VTI",            name: "Vanguard Total Market ETF" },
  { ticker: "GLD",            name: "Gold ETF (SPDR)" },
  { ticker: "ARKK",           name: "ARK Innovation ETF" },
  // ── Crypto ────────────────────────────────────────────
  { ticker: "BTC-USD",        name: "Bitcoin" },
  { ticker: "ETH-USD",        name: "Ethereum" },
  { ticker: "BNB-USD",        name: "BNB" },
  { ticker: "SOL-USD",        name: "Solana" },
  { ticker: "XRP-USD",        name: "XRP" },
  { ticker: "ADA-USD",        name: "Cardano" },
  { ticker: "AVAX-USD",       name: "Avalanche" },
  { ticker: "DOT-USD",        name: "Polkadot" },
  { ticker: "DOGE-USD",       name: "Dogecoin" },
  { ticker: "SHIB-USD",       name: "Shiba Inu" },
  { ticker: "MATIC-USD",      name: "Polygon (MATIC)" },
  { ticker: "LINK-USD",       name: "Chainlink" },
  { ticker: "UNI-USD",        name: "Uniswap" },
  { ticker: "ATOM-USD",       name: "Cosmos" },
  { ticker: "LTC-USD",        name: "Litecoin" },
];
